import { legalDocPathForNoteId } from './legalDocs'

export const HELP_CENTER_ROOT_ID = 'tQruG1jhfMO6Ab'

export interface HelpNoteSummary {
  id: string
  title: string
  iconShape: string | null
  iconColor: string | null
  parentNoteId: string | null
  lastEditedAt: string
  listPosition: number | null
}

export interface HelpNote extends HelpNoteSummary {
  content: string
}

export interface HelpNoteTreeNode extends HelpNoteSummary {
  children: HelpNoteTreeNode[]
}

export interface HelpNoteTreeRoot extends HelpNote {
  children: HelpNoteTreeNode[]
}

// Assembles the sidebar tree from a flat descendants listing (the API returns
// the subtree flattened; sibling order is preserved from the listing).
export function buildHelpNoteTreeFromFlat(
  root: HelpNoteSummary,
  notes: HelpNoteSummary[]
): HelpNoteTreeNode[] {
  const byParent = new Map<string, HelpNoteSummary[]>()
  for (const note of notes) {
    if (!note.parentNoteId) {
      continue
    }
    const siblings = byParent.get(note.parentNoteId) ?? []
    siblings.push(note)
    byParent.set(note.parentNoteId, siblings)
  }
  const build = (parentId: string): HelpNoteTreeNode[] =>
    (byParent.get(parentId) ?? []).map((note) => ({
      ...note,
      children: build(note.id),
    }))
  return [
    {
      ...root,
      children: build(root.id),
    },
  ]
}

export function slugifyHelpTitle(title: string): string {
  return title
    .trim()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function helpArticlePathParams(note: { id: string; title: string }): {
  id: string
  slug: string
} {
  return { id: note.id, slug: slugifyHelpTitle(note.title) }
}

// Legacy note ids can contain "~" (e.g. H1mLnkz6~)
const PUBLIC_NOTE_REDIRECT_PATTERN =
  /https:\/\/slite\.com\/api\/public\/notes\/([\w~-]+)\/redirect/g

// Unwraps the article body from the CSS-less HTML export (`css=none`) for
// in-page injection. The export keeps its per-document emotion <style> blocks
// inline, so those are preserved (the rest of the styling comes from
// `@slite/style`); only the non-functional <script> tags are dropped.
export function extractHelpArticleHtml(html: string): string {
  const body = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)?.[1] ?? html
  return body.replace(/<script[\s\S]*?<\/script>/gi, '').trim()
}

const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
}

function decodeHtmlEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (entity, code: string) => {
    if (code[0] === '#') {
      const isHex = code[1] === 'x' || code[1] === 'X'
      const codePoint = parseInt(code.slice(isHex ? 2 : 1), isHex ? 16 : 10)
      return codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : entity
    }
    return NAMED_HTML_ENTITIES[code.toLowerCase()] ?? entity
  })
}

// Collapses a fragment of exported HTML down to its visible text.
function htmlToText(fragment: string): string {
  return decodeHtmlEntities(fragment.replace(/<[^>]+>/g, ''))
    .replace(/﻿/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export interface HelpArticleHeading {
  id: string
  title: string
  level: number
}

// Headings in the export carry their editor block id as the DOM id, so the
// table of contents can anchor to them without rewriting the article markup.
// Only h1–h3 are collected (the TOC goes three levels deep).
export function extractHelpArticleHeadings(html: string): HelpArticleHeading[] {
  const headings: HelpArticleHeading[] = []
  for (const match of html.matchAll(/<h([1-3])\b([^>]*)>([\s\S]*?)<\/h\1>/gi)) {
    const id = match[2].match(/\bid="([^"]+)"/)?.[1]
    const title = htmlToText(match[3])
    if (id && title) {
      headings.push({ id, title, level: Number(match[1]) })
    }
  }
  return headings
}

// Note/card links (public redirect URLs) are rewritten to stay inside /help
// (or to a standalone legal page when the target is one), but only for notes
// that live in the help center — /help/<id> 404s for anything else, while the
// public redirect URL still resolves.
export function rewriteHelpNoteLinks(html: string): string {
  return html.replace(
    PUBLIC_NOTE_REDIRECT_PATTERN,
    (match, noteId: string) =>
      legalDocPathForNoteId(noteId) ?? `/help/${noteId}`
  )
}

// Sentinel characters injected around search matches by the Slite API
// (highlightPreTag/highlightPostTag) so the client can render emphasis
// without interpreting HTML from the search index.
export const HELP_SEARCH_HIGHLIGHT_PRE = '⁅'
export const HELP_SEARCH_HIGHLIGHT_POST = '⁆'

export interface HelpHighlightSegment {
  text: string
  isMatch: boolean
}

export function splitHelpHighlight(highlight: string): HelpHighlightSegment[] {
  const segments: HelpHighlightSegment[] = []
  for (const [index, part] of highlight
    .split(HELP_SEARCH_HIGHLIGHT_PRE)
    .entries()) {
    if (index === 0) {
      if (part) {
        segments.push({ text: part, isMatch: false })
      }
      continue
    }
    const [match, ...rest] = part.split(HELP_SEARCH_HIGHLIGHT_POST)
    if (match) {
      segments.push({ text: match, isMatch: true })
    }
    const tail = rest.join(HELP_SEARCH_HIGHLIGHT_POST)
    if (tail) {
      segments.push({ text: tail, isMatch: false })
    }
  }
  return segments
}
